import { useWidgetConfigState } from 'widgets/store/configuration/hooks';
import { GridColumnWidthState } from 'widgets/store/grid/columnWidth/GridColumnWidthState';
import { GridProps } from '@instinet-analytics/semantic-ui-components';
import { useDispatchUpdateWidgetColumnWidth } from 'widgets/store/grid/columnWidth/dispatcher';
import { ColDef } from 'ag-grid-community';
import { filterToVisibleColumns } from '@instinet-analytics/semantic-ui-components';
import { applyColumnWidths } from 'widgets/store/grid/columnWidth/utils';
import { GridVisibleColumnState } from 'widgets/store/grid/visibleColumns/GridVisibleColumnState';
import { useDispatchUpdateVisibleColumns } from 'widgets/store/grid/visibleColumns/dispatcher';
import {
    ColumnDefinitionsDictionary,
    isValidColumnDefinition,
} from '@instinet-analytics/semantic-ui-components';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from 'app/AppContext';
import { GridSortState } from 'widgets/store/grid/sorting/GridSortState';
import {
    FilterState,
    GridFiltersState,
    isColumnFilter,
} from 'widgets/store/grid/filters/FilterState';
import {
    useDispatchWidgetToggleColumnSort,
    useDispatchWidgetToggleSingleColumnSort,
} from 'widgets/store/grid/sorting/dispatcher';
import { applySortingToColumnDefinitions } from '@instinet-analytics/semantic-ui-components';
import { arraysDiffer } from 'utils/arrays/arrayUtils';

type GridState<TColumnId extends string> = Partial<
    GridVisibleColumnState<TColumnId> &
        GridFiltersState<TColumnId> &
        GridSortState<TColumnId> &
        GridColumnWidthState<TColumnId>
>;

/*
 * Returns Grid props calculated from grid related Widget state. These states are:
 * 1) Visible Columns
 * 2) Filtering
 * 3) Sorting
 * 4) Column Widths
 * Important: ensure the colDefs reference changes when the widget grid config changes
 */
export const useWidgetGridState = <TRowData, TColumnId extends string>(
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>,
    colDefs: ColDef[],
    options?: { disableSorting?: boolean }
) => {
    const context = useContext(AppContext);
    const configState = useWidgetConfigState<GridState<TColumnId>>();
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

    const visibleColumns = useMemo(
        () =>
            configState.visibleColumns
                ? configState.visibleColumns.filter((c) =>
                      isValidColumnDefinition(columnDefinitions, c)
                  )
                : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [configState.visibleColumns]
    );
    const filters = useMemo(
        () =>
            configState?.filters
                ? configState.filters.filter(
                      (f: FilterState<TColumnId>) =>
                          !isColumnFilter(f) ||
                          isValidColumnDefinition(columnDefinitions, f.column)
                  )
                : undefined,
        [configState?.filters, columnDefinitions]
    );
    const sort = useMemo(
        () =>
            configState.sort
                ? configState?.sort.filter((s) =>
                      isValidColumnDefinition(columnDefinitions, s.colId)
                  )
                : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [configState?.sort]
    );
    const columnWidths = configState?.columnWidths;

    const dispatchUpdateVisibleColumns =
        useDispatchUpdateVisibleColumns<TColumnId>();
    const dispatchSetColumnWidth =
        useDispatchUpdateWidgetColumnWidth<TColumnId>();
    const dispatchToggleColumnSort =
        useDispatchWidgetToggleColumnSort<TColumnId>();
    const dispatchToggleSingleColumnSort =
        useDispatchWidgetToggleSingleColumnSort<TColumnId>();

    useEffect(() => {
        // Apply visible columns to column defs...
        const nextColumnDefs = visibleColumns
            ? filterToVisibleColumns(colDefs, visibleColumns)
            : colDefs;

        // Apply column widths to column defs...
        columnWidths && applyColumnWidths(nextColumnDefs, columnWidths);

        // Extract list of columns that have a filter applied...
        const filteredColumns: TColumnId[] = filters
            ? filters.filter(isColumnFilter).map((f) => f.column)
            : [];

        // Apply sorting to column definitions
        // (uses columns widths and list if filtered columns, so this is done last)
        if (!options?.disableSorting) {
            sort &&
                applySortingToColumnDefinitions({
                    columnDefinitions,
                    columnDefs: nextColumnDefs,
                    sort,
                    context,
                    filteredColumns,
                    onSortToggle: dispatchToggleColumnSort,
                    onSingleSortToggle: dispatchToggleSingleColumnSort,
                });
        }

        setColumnDefs(nextColumnDefs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        colDefs,
        columnDefinitions,
        sort,
        context,
        visibleColumns,
        columnWidths,
        filters,
        context.currencyFormat.exchangeRate,
    ]);

    const onColumnResized = useCallback(
        (columnId: string, width: number) =>
            dispatchSetColumnWidth(columnId as TColumnId, width),
        [dispatchSetColumnWidth]
    );

    const onColumnMoved = useCallback(
        (columns: string[]) => {
            if (
                !visibleColumns ||
                arraysDiffer(columns, visibleColumns, false)
            ) {
                dispatchUpdateVisibleColumns(columns as TColumnId[]);
            }
        },
        [dispatchUpdateVisibleColumns, visibleColumns]
    );

    // Configure grid props...
    const gridPropsFromState: Partial<GridProps<TRowData>> = {};
    if (configState.columnWidths) {
        gridPropsFromState.onColumnResized = onColumnResized;
        gridPropsFromState.autoSizeColumns = false;
    }
    if (configState.visibleColumns) {
        gridPropsFromState.onColumnMoved = onColumnMoved;
    }
    if (configState.sort) {
        gridPropsFromState.sort = sort;
    }

    return {
        gridPropsFromState,
        columnDefs,
        visibleColumns,
        filters,
        sort,
    };
};
